<template>
    <div class="cart-content" :class="[mode, planPromotionText ? 'has-promotion' : '']">
        <div class="card-header" v-if="!isShowCompleteLayout">
            <div class="title-block">
                <span class="card-title">{{ plan.series?.i18n.name }}</span>
                <reo-tip-dialog
                    ref="planTipDialog"
                    v-if="plan.style && !notStoragePlan"
                    :isBottomWhenMobile="true"
                >
                    <div class="plan-tip-dialog-content">
                        <div class="plan-dialog-body">
                            <div class="plan-desc" v-html="planTipText"></div>
                            <p class="plan-detail">
                                {{ $t('dashboard.storageRegion') }}: {{ plan.storageRegion }}
                                <br />
                                {{ $t('dashboard.suitableDevices') }}:
                                {{ plan.productSupportedString }}
                            </p>
                        </div>
                        <div class="plan-dialog-footer">
                            <button class="plan-dialog-btn" @click="closePlanTipDialog">
                                {{ $t('common.got_it') }}
                            </button>
                        </div>
                    </div>
                </reo-tip-dialog>
            </div>

            <div v-if="!isMobile" class="price-block">
                <span class="price__current" v-if="isPriceDifference">
                    {{ adjustmentInfo.currency }} {{ adjustmentInfo.amount | toFixed2 }}
                </span>
                <template v-else>
                    <span class="price__current">
                        {{ plan.pricing.currency }} {{ $options.filters?.toFixed2(orderItem?.salePriceAmount ?? (Number(plan?.pricing.salePrice ?? 0) * quantity)) }}
                    </span>
                    <del class="price__regular" v-if="hasDiscountPrice">
                        {{ plan.pricing.currency }} {{ $options.filters?.toFixed2(orderItem?.regularAmount ?? (Number(plan?.pricing.regularPrice ?? 0) * quantity)) }}
                    </del>
                </template>
            </div>
        </div>

        <div class="card-divider" v-if="!isShowCompleteLayout"></div>

        <div v-if="isShowCompleteLayout && assignmentLabel && assignmentValue" class="detail-row">
            <span class="detail-label">{{ assignmentLabel }}</span>
            <span class="detail-value">{{ assignmentValue }}</span>
        </div>

        <div v-if="isShowCompleteLayout" class="detail-row detail-row--plan-summary">
            <div class="detail-label detail-label--plan">
                <span>{{ plan.series?.i18n.name }}</span>
            </div>
            <div class="detail-value plan-summary-price">
                <span v-if="isPriceDifference" class="price__current price__current--summary">
                    {{ adjustmentInfo.currency }} {{ adjustmentInfo.amount | toFixed2 }}
                </span>
                <template v-else>
                    <span class="price__current price__current--summary">
                        {{ plan.pricing.currency }} {{ $options.filters?.toFixed2(plan?.pricing.salePrice ) }}
                    </span>
                    <del class="price__regular price__regular--summary" v-if="hasDiscountPrice">
                        {{ plan.pricing.currency }} {{ $options.filters?.toFixed2(plan?.pricing.regularPrice ) }}
                    </del>
                </template>
            </div>
        </div>

        <div v-if="showPlanSummaryCycleRow" class="detail-row">
            <span class="detail-label">{{ cycleLabel }}</span>
            <span class="detail-value">{{ cycleValue }}</span>
        </div>

        <div v-if="!error" class="detail-row">
            <span class="detail-label">{{ $t('cart.plan.valid') }}:</span>
            <span class="detail-value" >{{ validTimeText }}</span>
        </div>

        <div v-if="!isShowCompleteLayout && assignmentLabel && assignmentValue && !hideDeviceAssign" class="detail-row">
            <span class="detail-label">{{ assignmentLabel }}</span>
            <span class="detail-value">{{ assignmentValue }}</span>
        </div>

        <div v-else-if="mode !== 'show' && !hideDeviceAssign" class="detail-row">
            <span class="detail-label">{{ $t('cart.plan.relate_devices') }}:</span>
            <span class="detail-link" @click="$emit('selectDevice')">{{ $t('selectProduct.checkList') }}</span>
        </div>

        <div v-if="showAutoRenew" class="detail-row" :class="{ 'detail-row--switch': mode !== 'show' }">
            <span class="detail-label">{{ $t('dashboard.device_detail.auto_renewal') }}:</span>
            <span
                v-if="mode === 'show'"
                class="detail-value"
            >{{ autoRenew ? $t('common.on') : $t('common.off') }}</span>
            <div v-else class="switch-wrapper">
                <ElSwitch
                    :value="autoRenew"
                    class="auto-renew-switch"
                    active-color="var(--b_6)"
                    :width="isMobile ? 32 : 36"
                    @change="handleAutoRenewChange"
                />
            </div>
        </div>

        <div v-if="showCycleRow && !isShowCompleteLayout" class="detail-row detail-row--action">
            <div class="detail-label detail-label--tip">
                <span>{{ cycleLabel }}</span>
                <reo-tip>
                    <span v-if="isAddOn">{{ $t('cart.add_on_quantity_note') }}</span>
                    <span v-else>{{ $t('cart.cycle_tip') }}</span>
                </reo-tip>
            </div>
            <div class="action-side">
                <div class="quantity" v-if="mode === 'edit'">
                    <button
                        @click="minus"
                        class="minus button_clear"
                        :class="{ disabled: quantity <= min }"
                    >
                        <minus-icon />
                    </button>
                    <input
                        type="number"
                        :value="quantity"
                        ref="quantityInput"
                        @change="change"
                        :disabled="loading"
                        :min="min"
                        :max="max"
                    >
                    <button
                        @click="plus"
                        class="plus button_clear"
                        :class="{ disabled: quantity >= max }"
                    >
                        <add-icon />
                    </button>
                </div>
                <span v-else class="duration-value">{{ cycleValue }}</span>
                <button v-if="mode === 'edit' && showDelete" class="delete button_clear" @click="showConfirmDelete = true">
                    <img :src="DeleteIcon">
                </button>
            </div>
        </div>

        <div v-if="isMobile && !isShowCompleteLayout" class="price-block price-block--mobile">
            <span class="price__current" v-if="isPriceDifference">
                {{ adjustmentInfo.currency }} {{ adjustmentInfo.amount | toFixed2 }}
            </span>
            <template v-else>
                <span class="price__current">
                    {{ plan.pricing.currency }} {{ $options.filters?.toFixed2(orderItem?.salePriceAmount ?? (Number(plan?.pricing.salePrice ?? 0) * quantity)) }}
                </span>
                <del class="price__regular" v-if="hasDiscountPrice">
                    {{ plan.pricing.currency }} {{ $options.filters?.toFixed2(orderItem?.regularAmount ?? (Number(plan?.pricing.regularPrice ?? 0) * quantity)) }}
                </del>
            </template>
        </div>

        <rich-text
            class="promotion-text"
            v-if="planPromotionText && isFirstCharge"
            :html="planPromotionText"
            :checkout-apply-promotion-context="checkoutApplyPromotionContext"
            :apply-promotion-applied="isPromotionUsageLimitAppliedOnCheckout"
            @click.stop="handlePromotionRichTextApplyClick"
        />

        <reo-dialog
            v-if="showConfirmDelete"
            class="delete-confirm-dialog"
            :title="$t('cart.confirm_delete')"
            :labelOfConfirm="$t('cancel_plan.confirm')"
            :labelOfCancel="$t('common.cancel')"
            :maxWidth="deleteDialogMaxWidth"
            @confirm="deleteSelf"
            @cancel="showConfirmDelete = false"
        >
            <div v-if="showDeleteDialogNote" class="delete-dialog-body">
                <p class="delete-dialog-note" v-html="$t('cart.refund_note')"></p>
                <ReoLink
                    class="delete-dialog-link"
                    href="/subscription-terms/"
                    target="_blank"
                    :text="$t('learnMore')"
                    :size="16"
                />
            </div>
        </reo-dialog>

        <reo-dialog
            v-if="showDisableAutoRenewDialog"
            close-icon-offset="20px"
            :title="$t('dashboard.device_detail.main_card.disable_renew_title')"
            :labelOfConfirm="$t('dashboard.device_detail.main_card.disable_renew_yes')"
            :labelOfCancel="$t('dashboard.device_detail.main_card.disable_renew_no')"
            switchButton
            @confirm="confirmDisableAutoRenew"
            @cancel="showDisableAutoRenewDialog = false"
        >
            <p class="disable-auto-renew-note">{{ $t('dashboard.device_detail.main_card.disable_renew_content') }}</p>
        </reo-dialog>
    </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import ReoTip from '@/basic/Tip.vue';
import ReoTipDialog from '@/components/ReoTipDialog.vue';
import DeleteIcon from '@/common/images/common/combo/co_delete.svg';
import { ESeriesCategory, IAccountDevice, IAdjustmentInfo, IOrderItem, IPlan, IPromotionItem, ISimCardItem, ITotalDevice } from '@reolink-cloud/interface';
import MinusIcon from './Svg/MinusIcon.vue';
import AddIcon from './Svg/AddIcon.vue';
import { timeResolve } from '@/common/js/date';
import ReoDialog from '@/basic/Dialog.vue';
import ReoLink from '@/basic/Link.vue';
import { ESubscribeMode } from '@/interface';
import { config } from 'CONFIG';
import { getClampedPromotionUsageLimitQty, getDeviceTitle, getPlanPromotionText, getPlanPromotionUsageLimitToItems } from '@/utils/business';
import { Switch as ElSwitch } from 'element-ui';

export default Vue.extend({
    components: {
        ReoTip,
        ReoTipDialog,
        MinusIcon,
        ReoDialog,
        ReoLink,
        ElSwitch,
        AddIcon
    },
    props: {
        plan: {
            type: Object as PropType<Record<string, any> & IPlan>
        },
        loading: {
            type: Boolean
        },
        startedAt: {
            type: Number
        },
        endingAt: {
            type: Number
        },
        deviceInfo: {
            type: Object as PropType<IAccountDevice | null>
        },
        min: {
            type: Number
        },
        max: {
            type: Number
        },
        quantity: {
            type: Number,
            default: 1
        },
        mode: {
            type: String as PropType<'edit'|'show'>,
            default: 'edit'
        },
        adjustmentInfo: {
            type: Object as PropType<IAdjustmentInfo>
        },
        simCard: {
            type: Object as PropType<ISimCardItem|undefined>,
            required: false
        },
        showDelete: {
            type: Boolean,
            default: false
        },
        showAutoRenew: {
            type: Boolean,
            default: false
        },
        autoRenew: {
            type: Boolean,
            default: false
        },
        needPay: {
            type: Boolean,
            default: true
        },
        lockDeviceData: Object as PropType<ITotalDevice>,
        subscribeMode: String as PropType<ESubscribeMode>,
        promotions: {
            type: Array as PropType<IPromotionItem[]>,
            default: () => []
        },
        orderItem: {
            type: Object as PropType<IOrderItem>
        },
        /** 为 true 时根据营销活动 usageLimitToItems 同步数量（仅套餐页促销文案内按钮跳转购物车场景） */
        applyPromotionUsageLimitToQty: {
            type: Boolean,
            default: false
        },
        /** 购物车/结账页 RichText，用于 apply-button 文案与「已应用」态 */
        checkoutApplyPromotionContext: {
            type: Boolean,
            default: false
        },
        hideDeviceAssign: {
            type: Boolean,
            default: false
        }
    },
    model: {
        prop: 'quantity',
        event: 'change'
    },
    data() {

        return {
            error: false,
            DeleteIcon,
            showConfirmDelete: false,
            showDisableAutoRenewDialog: false,
            aiFeatures: config.aiFeatures,
        };

    },
    computed: {
        isAddOn(): boolean {

            return this.plan.series?.category === ESeriesCategory.CLOUD_DATA_ADD_ON_PLAN;

        },
        unit(): string {

            return this.$tc( `cycle_unit.${this.plan.duration.unit}`, this.plan.duration.amount, {
                count: this.plan.duration.amount
            }).toString();

        },
        isMobile ( ): boolean {

            return  this.$store.getters.isMobile;

        },
        isPriceDifference(): boolean {

            return Boolean( this.adjustmentInfo );

        },
        isShowCompleteLayout(): boolean {

            return this.mode === 'show';

        },
        /** 完成页单独一行展示周期/补充包数量，仅在大于 1 个计费周期时显示（数量或套餐时长任一侧 > 1） */
        showPlanSummaryCycleRow(): boolean {

            if ( !this.isShowCompleteLayout || this.isFreePlan ) {

                return false;

            }

            return this.quantity >= 1 && !this.isPriceDifference;

        },
        isNoPriceDifference(): boolean {

            if ( this.subscribeMode === 'switch' ) {

                return this.plan.series?.category !== ESeriesCategory.CLOUD_DATA_PLAN && this.plan.series?.category !== ESeriesCategory.CLOUD_STORAGE_DATA_PLAN;

            } else {

                return true;

            }

        },
        validStartTime(): string {

            let {

                year,
                month,
                dateOfMonth,
                hourOf12Decimal,
                noon

            } = timeResolve( this.startedAt );

            let time =  `${hourOf12Decimal}:00 ${noon}`;

            return `${this.$t( `date_format.default`, {
                year,
                month,
                day: dateOfMonth
            }) as string} ${time}`;

        },

        validEndTime(): string {

            let {

                year,
                month,
                dateOfMonth,
                hourOf12Decimal,
                noon

            } = timeResolve( this.endingAt );

            let time =  `${hourOf12Decimal}:00 ${noon}`;

            return `${this.$t( `date_format.default`, {
                year,
                month,
                day: dateOfMonth
            }) as string} ${time}`;

        },
        notStoragePlan(): boolean {

            if ( !this.plan.series?.category ) {

                return false;

            }

            return ![ESeriesCategory.CLOUD_STORAGE_PLAN, ESeriesCategory.CLOUD_STORAGE_DATA_PLAN].includes( this.plan.series?.category );

        },
        isFreePlan(): boolean {

            return parseFloat( this.plan.pricing.salePrice ) === 0;

        },
        lockedDeviceTitle(): string {

            return getDeviceTitle( this.lockDeviceData );

        },
        validTimeText(): string {

            if ( this.startedAt && this.endingAt ) {

                return `${this.validStartTime} - ${this.validEndTime}`;

            }

            return this.$t( 'cart.unknow_time' ).toString();

        },
        assignmentLabel(): string | undefined {

            if ( this.lockDeviceData ) {

                return this.$t( 'cancel_plan.plan_display.device_assigned' ).toString();

            }

            if ( this.deviceInfo ) {

                return this.$t( 'cancel_plan.plan_display.device_assigned' ).toString();

            }

            if ( this.simCard ) {

                return this.$t( 'cloud.common.simCard' ).toString();

            }

            return undefined;

        },
        assignmentValue(): string | undefined {

            if ( this.lockDeviceData ) {

                return this.$t( 'my_sim.device_string', {
                    model: this.lockDeviceData.model,
                    uid: this.lockDeviceData.uid,
                    nickName: this.lockedDeviceTitle
                }).toString();

            }

            if ( this.deviceInfo ) {

                const channels = this.deviceInfo.channels ?? [];
                let title = '';

                if ( channels.length > 1 ) {

                    title = channels[channels.length - 1]?.title ?? '';

                } else if ( channels.length === 1 ) {

                    title = channels[0]?.title ?? '';

                } else {

                    title = this.deviceInfo.model?.title ?? this.deviceInfo.uid;

                }

                return this.$t( 'dashboard.device_detail.device_string', {
                    model: this.deviceInfo.model?.title,
                    uid: this.deviceInfo.uid,
                    nickName: title
                }).toString();

            }

            if ( this.simCard ) {

                return this.simCard.simCardCode;

            }

            return undefined;

        },
        hasDiscountPrice(): boolean {

            return Number( this.plan?.pricing?.regularPrice ) !== Number( this.plan?.pricing?.salePrice );

        },
        showCycleRow(): boolean {

            return !this.isFreePlan && !this.isPriceDifference;

        },
        cycleLabel(): string {

            if ( this.isAddOn ) {

                return this.$tc( 'cart.add_on_quantity' );

            }

            return this.$tc( 'cart.term_of_plan' );

        },
        cycleValue(): string {

            if ( this.isAddOn ) {

                return `${this.quantity}`;

            }

            return `${this.quantity} ${this.unit}`;

        },
        planPromotionText(): string | undefined {

            if ( !this.plan ) {

                return undefined;

            }

            return getPlanPromotionText( this.promotions, this.plan );

        },
        promotionUsageLimit(): number | undefined {

            if ( !this.applyPromotionUsageLimitToQty ) return undefined;
            return getPlanPromotionUsageLimitToItems( this.promotions, this.plan );

        },
        isPromotionUsageLimitAppliedOnCheckout(): boolean {

            if ( !this.checkoutApplyPromotionContext || !this.applyPromotionUsageLimitToQty ) {

                return false;

            }
            const limit = this.promotionUsageLimit;
            if ( limit == null ) {

                return false;

            }
            return this.quantity >= limit;

        },
        planTipText(): string {

            if ( !this.plan?.style ) {

                return '';

            }

            const comma = this.$t( 'comma' ) as string;
            const items: string[] = [];
            const isRichPlan = this.plan.series?.category === ESeriesCategory.CLOUD_RICH_NOTIFICATION_PLAN;

            if ( !isRichPlan ) {

                const history = this.$options.filters?.planHistory?.( this.plan ) as string | undefined;
                if ( this.plan.style['cloud_storage.default_retention_day'] && history ) {

                    items.push( history );

                }

                const capacity = this.$options.filters?.planCapacity?.( this.plan ) as string | undefined;
                if ( this.plan.style['cloud_storage.space_capacity'] && capacity ) {

                    items.push( capacity );

                }

            }

            const camera = this.$options.filters?.planCamera?.( this.plan ) as string | undefined;
            if ( camera  && this.plan.series?.category !== ESeriesCategory.CLOUD_STORAGE_DATA_PLAN ) {

                items.push( camera );

            }

            for ( const feature of this.aiFeatures ) {

                if ( this.plan.style[`cloud_ai.${feature}`] ) {

                    items.push( this.$t( `cloud_ai.${feature}` ) as string );

                }

            }

            const richNotification = this.$options.filters?.planRichNotification?.( this.plan ) as string | undefined;
            if ( richNotification ) {

                items.push( richNotification );

            }

            const content = items.filter( Boolean ).join( comma + ' ' );
            return content ? `${content}.` : '';

        },
        isFirstCharge(): boolean {

            return this.orderItem?.context.isFirstCharge ?? false;

        },
        showDeleteDialogNote(): boolean {

            return Boolean( config.showRefundWarning );

        },
        deleteDialogMaxWidth(): string {

            return this.showDeleteDialogNote ? '888px' : '620px';

        }

    },
    watch: {
        promotionUsageLimit: {
            immediate: true,
            handler() {

                this.syncPromotionUsageLimitFromPlan();

            }
        }
    },
    methods: {
        minus() {

            if ( this.quantity > this.min ) {

                this.$emit( 'change', this.quantity - 1 );

            }

        },
        plus() {

            if ( this.quantity < this.max ) {

                this.$emit( 'change', this.quantity + 1 );

            }

        },
        change( e: Event ) {

            let value = parseInt(( e.target as HTMLInputElement ).value.trim());
            if ( Number.isNaN( value )) {

                value = this.min;

            }
            let finalQuantity = value;
            if ( value % 1 !== 0 ) {

                finalQuantity = Math.round( value );

            } else if ( value > this.max ) {

                finalQuantity = this.max;

            } else if ( value < this.min ) {

                finalQuantity = this.min;

            }
            ( e.target as HTMLInputElement ).value = finalQuantity.toString();
            this.$emit( 'change', finalQuantity );

        },
        deleteSelf() {

            this.showConfirmDelete = false;
            this.$emit( 'delete' );

        },
        closePlanTipDialog() {

            ( this.$refs.planTipDialog as any ).close();

        },
        /**
         * 与 promotionUsageLimit 的 watch 共用；force 时在无 query 或 URL 未变时仍可同步数量（不依赖 router 触发 watch）。
         */
        syncPromotionUsageLimitFromPlan( options?: { force?: boolean; }): void {

            if ( !options?.force && !this.applyPromotionUsageLimitToQty ) {

                return;

            }
            const clamped = getClampedPromotionUsageLimitQty(
                this.promotions,
                this.plan,
                this.min ?? 1,
                this.max
            );
            if ( clamped == null ) {

                return;

            }
            if ( clamped !== this.quantity ) {

                this.$emit( 'change', clamped );

            }

        },
        handlePromotionRichTextApplyClick( _e: MouseEvent, dataAction: string | null ) {

            if ( dataAction !== 'apply-promotion' ) {

                return;

            }
            if ( this.isPromotionUsageLimitAppliedOnCheckout ) {

                return;

            }
            this.syncPromotionUsageLimitFromPlan({ force: true });
            this.$router.replace({
                query: {
                    ...this.$route.query,
                    apply_promotion_usage_limit: '1'
                } as Record<string, string | string[]>
            }).catch( console.error );

        },
        handleAutoRenewChange( value: boolean ) {

            if ( value ) {

                this.$emit( 'update:autoRenew', true );
                return;

            }

            this.showDisableAutoRenewDialog = true;

        },
        confirmDisableAutoRenew() {

            this.showDisableAutoRenewDialog = false;
            this.$emit( 'update:autoRenew', false );

        }

    },
});
</script>

<style lang="less" scoped>
.cart-content {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 20px;
    background-color: #F3F4F5;
    border-radius: 8px;
    @media @mobile {
        padding: 16px;
        background-color: #FDFDFD;
    }
    &.has-promotion {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        margin-bottom: 47px;
    }
    & > div {
        min-width: 0;
    }
    .price__regular{
        font-family: @font_02;
        font-size: 12px;
        font-weight: 500;
        line-height: 1.4;
        color: @font_tertiary;
        text-decoration: line-through;
        @media @snapeak_mobile{
            font-size: 12px;
        }
    }
    .price__current{
        font-family: @font_02;
        font-size: 18px;
        font-weight: 600;
        line-height: 1.4;
        color: @font_primary;
        @media @snapeak_mobile{
            font-size: 16px;
        }
    }
    .card-header{
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
    }
    .title-block{
        min-width: 0;
        flex: 1;
        display: flex;
        align-items: flex-start;
        gap: 4px;
        /deep/ .reo-tip,
        /deep/ .reo-tip-dialog{
            margin-top: 3px;
            flex-shrink: 0;
        }
    }
    .card-title{
        font-family: @font_02;
        font-size: 18px;
        font-weight: 500;
        line-height: 1.4;
        color: @font_primary;
        white-space: normal;
        word-break: break-word;
        @media @snapeak_mobile{
            font-size: 16px;
            line-height: 1.4;
        }
    }
    .card-divider{
        height: 1px;
        background-color: #E1E1E1;
        margin: 4px 0;
    }
    .price-block{
        flex-shrink: 0;
        display: inline-flex;
        align-items: baseline;
        justify-content: flex-end;
        gap: 4px;
        flex-wrap: wrap;
        margin-left: auto;
        &--mobile{
            margin-top: 16px;
            width: 100%;
            margin-left: 0;
            border-top: 1px solid @line;
            padding-top: 16px;
        }
    }
    .detail-row{
        display: flex;
        align-items: flex-start;
        gap: 36px;
        min-width: 0;
        &--action{
            align-items: center;
        }
        &--switch{
            align-items: center;
        }
    }
    .detail-label,
    .detail-value,
    .duration-value,
    .detail-link{
        font-family: @font_02;
        font-size: 16px;
        line-height: 1.4;
        @media @snapeak_mobile{
            font-size: 12px;
            line-height: 1.4;
        }
    }
    .detail-label{
        width: 200px;
        flex-shrink: 0;
        font-weight: 400;
        color: #333333;
        &--tip{
            display: flex;
            align-items: center;
            gap: 4px;
        }
    }
    .detail-value,
    .duration-value{
        flex: 1;
        min-width: 0;
        font-weight: 500;
        word-break: break-word;
        color: #333333;
    }
    .detail-row .detail-value.detail-value--highlight{
        color: #FF8836;
        font-weight: 500;
    }
    .detail-row--plan-summary{
        align-items: flex-start;
    }
    .detail-label--plan{
        min-width: 0;
        /deep/ .reo-tip{
            vertical-align: middle;
        }
        > span{
            flex: 1;
            min-width: 0;
            word-break: break-word;
        }
    }
    .plan-summary-price{
        display: flex;
        align-items: baseline;
        flex-wrap: wrap;
        gap: 4px;
        flex: 1;
        min-width: 0;
        justify-content: flex-start;
    }
    .price__current--summary{
        font-size: 16px;
        font-weight: 500;
        line-height: 1.4;
        color: #FF8836;
        @media @snapeak_mobile{
            font-size: 12px;
        }
    }
    .price__regular--summary{
        font-size: 12px;
        font-weight: 500;
        line-height: 1.4;
        color: @font_secondary;
        @media @snapeak_mobile{
            font-size: 12px;
        }
    }
    .detail-link{
        flex: 1;
        min-width: 0;
        color: #0050E2;
        font-weight: 500;
        text-decoration: underline;
        cursor: pointer;
    }
    .action-side{
        display: flex;
        align-items: center;
        gap: 12px;
        flex: 1;
        min-width: 0;
    }
    .switch-wrapper{
        display: flex;
        align-items: center;
        flex: 1;
        min-width: 0;
    }
    .disable-auto-renew-note{
        font-family: @font_02;
        font-size: 16px;
        font-weight: 500;
        line-height: 1.5;
        color: #3D3D3D;
    }
    .quantity {
        display: inline-flex;
        align-items: center;
        justify-content: space-between;
        width: 118px;
        min-width: 118px;
        height: 30px;
        padding: 4px;
        border: 1px solid #D7D9DC;
        border-radius: 4px;
        box-sizing: border-box;
        background-color: transparent;
        input {
            width: 22px;
            height: 22px;
            padding: 0;
            border: none;
            background-color: transparent;
            color: @font_primary;
            font-family: @font_02;
            font-size: 14px;
            font-weight: 500;
            line-height: 22px;
            text-align: center;
            -webkit-appearance: textfield;
            appearance: textfield;
            margin: 0;
        }
        button {
            width: 22px;
            height: 22px;
            padding: 0;
            border: none;
            background-color: transparent;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            color: @font_secondary;
        }
        .disabled {
            color: @disable;
            /deep/ svg{
                fill: @disable;
            }
        }
    }
    /deep/ .tip-icon {
        vertical-align: baseline;
    }
    .delete{
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        img{
            width: 18px;
            height: 18px;
        }
    }
    .delete-dialog-body{
        display: flex;
        flex-direction: column;
        gap: 12px;
    }
    .delete-dialog-note{
        font-family: @font_02;
        font-size: 16px;
        font-weight: 500;
        line-height: 1.5;
        color: #3D3D3D;
    }
    .delete-dialog-link{
        align-self: flex-start;
        /deep/ .text{
            text-decoration: underline;
        }
    }
    .promotion-text{
        font-size: 16px;
        color: @orange_4;
        padding: 12px 24px;
        background-color: rgba(247, 138, 49, 0.1);
        border-bottom-left-radius: @border-radius-level-button;
        border-bottom-right-radius: @border-radius-level-button;
        position: absolute;
        bottom: -49px;
        left: 0;
        width: 100%;
        box-sizing: border-box;
        .mobile({
            font-size: 12px;
            bottom: -42px;
        });
    }
    @media @snapeak_mobile{
        .detail-row{
            gap: 12px;
            justify-content: space-between;
        }
        .detail-label{
            width: auto;
            max-width: 40%;
        }
        .detail-value{
            text-align: right;
        }
        .plan-summary-price{
            justify-content: flex-end;
        }
        .detail-row--action{
            align-items: flex-start;
        }
        .detail-row--switch{
            align-items: center;
        }
        .detail-row--switch .switch-wrapper{
            justify-content: flex-end;
        }
        .action-side{
            justify-content: flex-end;
            gap: 8px;
        }
        .quantity{
            width: 118px;
            min-width: 118px;
        }
        .disable-auto-renew-note{
            font-size: 14px;
        }
        .delete-dialog-note{
            font-size: 14px;
        }
    }
}

.show{
    .price-block{
        &--mobile{
            margin-top: 0;

        }
    }
}

.priceDifference{
    .rowExtra{
        font-size: 20px;
        font-weight: 600;
        line-height: normal;
        padding-bottom: 8px;
        border-bottom: 1px solid @line_light;
        display: flex;
        align-items: center;
        gap: 4px;
    }
    .card-divider{
        display: none;
    }
    .card-title{
            font-size: 16px;
            font-weight: 500;
            @media @snapeak_mobile{
                font-size: 12px;
            }
    }
    .detail-row--plan-summary .detail-label--plan > span{
        font-size: 16px;
        font-weight: 500;
        line-height: 1.4;
        @media @snapeak_mobile{
            font-size: 12px;
            line-height: 1.4;
        }
    }
}

.delete-confirm-dialog /deep/ .reo-dialog-inner{
    @media @pc{
        max-width: var(--max-width);
    }
}

.title-block /deep/ .reo-tip-dialog {
    .reo-dialog-inner {
        @media @pc {
            --dialog-padding-top: 20px;
            --dialog-padding-right: 20px;
            padding: 48px;
        }
        @media @mobile {
            --dialog-padding-top: 12px;
            --dialog-padding-right: 12px;
            padding: 32px 20px;
        }
    }
}

.plan-tip-dialog-content {
    display: flex;
    flex-direction: column;
    gap: 32px;
    .plan-dialog-body {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }
    .plan-desc {
        color: @font_primary;
        font-size: 20px;
        font-weight: 500;
        line-height: 1.4;
    }
    .plan-detail {
        color: @font_primary;
        font-size: 16px;
        line-height: 1.4;
        margin: 0;
    }
    .plan-dialog-footer {
        display: flex;
        justify-content: flex-end;
    }
    .plan-dialog-btn {
        height: 48px;
        min-width: 120px;
        padding: 0 24px;
        background: @b_6;
        border: none;
        border-radius: 8px;
        color: @white;
        font-size: 16px;
        font-weight: 500;
        line-height: 1.4;
        cursor: pointer;
        @media @mobile {
            width: 100%;
        }
    }
}

</style>
